import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import ProductScrapeModal from '../components/ProductScrapeModal';
import ProductSearchDropdown from '../components/ProductSearchDropdown';
import EditVariantModal, { VARIANT_API, variantHeaders } from '../components/EditVariantModal';
import EditProductModal from '../components/EditProductModal';
import { Plus, Package, Tag, Grid3x3, Search, Trash2, RefreshCw, Eye, Upload, Download, FileText, CheckCircle, AlertCircle, X, Edit, Zap } from 'lucide-react';

const ProductManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tab from URL query parameter, default to 'products'
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'products';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [variantError, setVariantError] = useState('');
  const onVariantSaved = (updated) => {
    setVariants((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
    setFilteredVariants((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
  };
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Mobile Phones');
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [togglingProductId, setTogglingProductId] = useState(null);

  const handleToggleProductStatus = async (productId, currentIsActive) => {
    try {
      setTogglingProductId(productId);
      const newStatus = !currentIsActive;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/${productId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ is_active: newStatus })
        }
      );
      const data = await response.json();
      if (response.ok) {
        // Update local state smoothly
        setProducts(prev => prev.map(p => p.id === productId ? { ...p, is_active: newStatus, live_date: newStatus ? new Date().toISOString() : p.live_date } : p));
        setFilteredProducts(prev => prev.map(p => p.id === productId ? { ...p, is_active: newStatus, live_date: newStatus ? new Date().toISOString() : p.live_date } : p));
      } else {
        alert('Failed to update status: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update product status');
    } finally {
      setTogglingProductId(null);
    }
  };

  // Bulk Upload State
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/import/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to download template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zobox_product_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert('Error downloading template: ' + err.message);
    }
  };

  const handleUploadCSV = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      setBulkError('Please select a CSV file to upload.');
      return;
    }
    setBulkUploading(true);
    setUploadProgress(0);
    setBulkError('');
    setBulkResult(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', bulkFile);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/import/csv`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: event => setUploadProgress(event.total ? Math.round(event.loaded * 100 / event.total) : 0),
      });
      setBulkResult(response.data);
      fetchProducts();
    } catch (err) {
      setBulkError(err.response?.data?.detail || err.message || 'Failed to upload CSV');
    } finally {
      setBulkUploading(false);
    }
  };

  const categories = ['Mobile Phones', 'Laptops', 'Tablet PC', 'Accessories'];

  // Handle tab change and update URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/products?tab=${tab}`, { replace: true });
  };

  // Fetch brands on mount and when category changes (needed for Add Product modal)
  useEffect(() => {
    fetchBrands();
  }, [selectedCategory]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
      fetchBrands(); // Fetch brands for tabs
    } else if (activeTab === 'variants') {
      fetchVariants();
    }
  }, [activeTab, selectedCategory]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/brands?category=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products?category=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      setProducts(data.products || []);
      applyBrandFilter(data.products || [], selectedBrand); // Apply brand filter
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyBrandFilter = (productsList, brandFilter) => {
    if (brandFilter === 'all') {
      setFilteredProducts(productsList);
    } else {
      const filtered = productsList.filter(p => p.brand_id === brandFilter);
      setFilteredProducts(filtered);
    }
  };

  const handleBrandChange = (brandId) => {
    setSelectedBrand(brandId);
    setSelectedProducts([]); // Clear selections when changing brand
    applyBrandFilter(products, brandId);
  };

  const fetchVariants = async () => {
    try {
      setLoading(true); setVariantError('');
      const response = await fetch(VARIANT_API, { headers: variantHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not load variants');
      setVariants(data.variants || []);
      setFilteredVariants(data.variants || []);
    } catch (error) {
      setVariantError(error.message || 'Could not load variants');
    } finally {
      setLoading(false);
    }
  };

  // Search handler - searches across brand, model, SKU, ZUPC
  const handleSearch = () => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      // If search is empty, show all
      setFilteredProducts(products);
      setFilteredVariants(variants);
      return;
    }

    if (activeTab === 'products') {
      // Search products by brand, model, SKU
      const filtered = products.filter(product => {
        const brandMatch = product.brand_name?.toLowerCase().includes(query);
        const modelMatch = product.model_name?.toLowerCase().includes(query);
        const skuMatch = product.sku_code?.toLowerCase().includes(query);
        
        return brandMatch || modelMatch || skuMatch;
      });
      setFilteredProducts(filtered);
    } else if (activeTab === 'variants') {
      // Search variants by ZUPC, brand, model
      const filtered = variants.filter(variant => {
        const zupcMatch = variant.zupc_code?.toLowerCase().includes(query);
        const brandMatch = variant.sku_details?.brand_name?.toLowerCase().includes(query);
        const modelMatch = variant.sku_details?.model_name?.toLowerCase().includes(query);
        const skuMatch = variant.sku_id?.toLowerCase().includes(query);
        
        return zupcMatch || brandMatch || modelMatch || skuMatch;
      });
      setFilteredVariants(filtered);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredProducts(products);
    setFilteredVariants(variants);
  };

  // Handle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  // Bulk activate/deactivate
  const handleBulkStatusChange = async (isActive) => {
    if (selectedProducts.length === 0) {
      alert('Please select products first');
      return;
    }

    if (!window.confirm(`${isActive ? 'Activate' : 'Deactivate'} ${selectedProducts.length} product(s)?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/products/bulk-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            product_ids: selectedProducts,
            is_active: isActive
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`✓ ${data.message}`);
        setSelectedProducts([]);
        fetchProducts(); // Reload
      } else {
        alert('Failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update products');
    } finally {
      setBulkLoading(false);
    }
  };

  // Group variants by SKU and ZUPC for hierarchical display
  const getInventoryHierarchy = (variantsToUse = variants) => {
    const hierarchy = {};
    
    variantsToUse.forEach(variant => {
      const skuName = variant.sku_details?.model_name || 'Unknown';
      const skuId = variant.sku_id;
      
      if (!hierarchy[skuId]) {
        hierarchy[skuId] = {
          name: skuName,
          totalQty: 0,
          zupcs: {}
        };
      }
      
      if (!hierarchy[skuId].zupcs[variant.zupc_code]) {
        hierarchy[skuId].zupcs[variant.zupc_code] = {
          zupc: variant.zupc_code,
          memory: variant.memory || variant.storage,
          color: variant.color,
          totalQty: 0,
          grades: []
        };
      }
      
      hierarchy[skuId].zupcs[variant.zupc_code].grades.push(variant);
      hierarchy[skuId].zupcs[variant.zupc_code].totalQty += variant.stock_quantity || 0;
      hierarchy[skuId].totalQty += variant.stock_quantity || 0;
    });
    
    return hierarchy;
  };

  const handleScrapeBrands = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/brands/scrape`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ category: selectedCategory })
        }
      );
      const data = await response.json();
      alert(data.message);
      fetchBrands();
    } catch (error) {
      console.error('Error scraping brands:', error);
      alert('Failed to scrape brands');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/brands/${brandId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      const data = await response.json();
      alert(data.message);
      fetchBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      alert('Failed to delete brand');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
            <p className="text-gray-600 mt-1">Manage brands, SKUs, and product variants (ZUPC)</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 flex items-center gap-2 font-medium text-sm transition-colors shadow-sm"
              title="Download official CSV template"
            >
              <Download className="w-4 h-4 text-gray-600" />
              Download Template
            </button>
            <button
              onClick={() => { setShowBulkModal(true); setBulkResult(null); setBulkError(''); setBulkFile(null); }}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2669] flex items-center gap-2 font-medium text-sm transition-colors shadow-sm"
            >
              <Upload className="w-4 h-4 text-white" />
              Bulk Product Upload
            </button>
          </div>
        </div>

        {/* Category Selector */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-border">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('products')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'products'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Package className="w-5 h-5" />
              Products (SKU)
            </button>
            <button
              onClick={() => handleTabChange('brands')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'brands'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Tag className="w-5 h-5" />
              Brands
            </button>
            <button
              onClick={() => handleTabChange('variants')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'variants'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Grid3x3 className="w-5 h-5" />
              Variants (ZUPC)
            </button>
          </nav>
        </div>

        {/* Brand Tabs - For Products tab */}
        {activeTab === 'products' && brands.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow overflow-hidden">
            <div className="border-b border-border">
              <div className="flex overflow-x-auto">
                {/* All Brands Tab */}
                <button
                  onClick={() => handleBrandChange('all')}
                  className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                    selectedBrand === 'all'
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  All Brands
                  <span className="ml-2 text-xs bg-muted px-2 py-1 rounded">
                    {products.length}
                  </span>
                </button>

                {/* Individual Brand Tabs */}
                {brands.map(brand => {
                  const brandProductCount = products.filter(p => p.brand_id === brand.id).length;
                  if (brandProductCount === 0) return null; // Hide brands with no products
                  
                  return (
                    <button
                      key={brand.id}
                      onClick={() => handleBrandChange(brand.id)}
                      className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${
                        selectedBrand === brand.id
                          ? 'border-primary text-primary bg-primary/5'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {brand.logo_url && (
                        <img 
                          src={brand.logo_url} 
                          alt={brand.name}
                          className="w-5 h-5 object-contain"
                        />
                      )}
                      {brand.name}
                      <span className="ml-1 text-xs bg-muted px-2 py-1 rounded">
                        {brandProductCount}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Search Bar - Autocomplete Dropdown for Products tab */}
        {activeTab === 'products' && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <label className="block text-sm font-medium text-foreground mb-2">
              Search Products
            </label>
            <ProductSearchDropdown
              products={filteredProducts}
              onSelect={(product) => navigate(`/products/${product.id}`)}
              placeholder="Search by SKU Code or Product Name..."
            />
            <p className="text-xs text-muted-foreground mt-2">
              💡 Tip: Type to search, use ↑↓ arrows to navigate, Enter to select
            </p>
          </div>
        )}

        {/* Search Bar - Filter for Variants tab */}
        {activeTab === 'variants' && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by ZUPC, Brand, Model, or SKU..."
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <Search className="w-5 h-5 text-muted-foreground absolute left-3 top-2.5" />
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                Search
              </button>
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-muted-foreground mt-2">
                Found {filteredVariants.length} variant(s)
              </p>
            )}
          </div>
        )}

        {/* Brands Tab */}
        {activeTab === 'brands' && (
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Brand Master - {selectedCategory}</h2>
              <p className="text-sm text-gray-600 mt-1">Brands are automatically populated when scraping products from GSMArena</p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading brands...</p>
              </div>
            ) : brands.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-12 text-center">
                <Tag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No brands found</h3>
                <p className="text-gray-600 mb-4">Click the button above to scrape brands from GSMArena</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {brands.map(brand => (
                  <div key={brand.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {brand.logo_url ? (
                          <img src={brand.logo_url} alt={brand.name} className="w-12 h-12 object-contain" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                            <Tag className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-900">{brand.name}</h3>
                          <p className="text-sm text-gray-500">{brand.category}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteBrand(brand.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Product Catalog</h2>
                <p className="text-sm text-muted-foreground">Browse products scraped from GSMArena</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowScrapeModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedProducts.length > 0 && (
              <div className="mb-4 bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
                <div className="text-sm font-medium text-foreground">
                  {selectedProducts.length} product(s) selected
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleBulkStatusChange(true)}
                    disabled={bulkLoading}
                    className="px-4 py-2 bg-success text-success-foreground rounded-lg hover:bg-success/90 text-sm disabled:opacity-50"
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => handleBulkStatusChange(false)}
                    disabled={bulkLoading}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 text-sm disabled:opacity-50"
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => setSelectedProducts([])}
                    className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 text-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Select All */}
            {filteredProducts.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedProducts.length === filteredProducts.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                />
                <label className="text-sm text-muted-foreground">
                  Select all ({filteredProducts.length})
                </label>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="bg-muted rounded-lg p-12 text-center">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {searchQuery ? 'No products match your search' : 'No products found'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? 'Try different search terms' : 'Start by scraping a product from GSMArena'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map(product => (
                  <div 
                    key={product.id} 
                    className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow relative ${
                      selectedProducts.includes(product.id) ? 'ring-2 ring-primary border-primary' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="absolute top-3 left-3 z-10">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleProductSelection(product.id);
                        }}
                        className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    {/* Product Content - Clickable */}
                    <div 
                      className="cursor-pointer ml-6"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <div className="flex gap-3">
                        {product.images && product.images[0] ? (
                          <img src={product.images[0]} alt={product.model_name} className="w-20 h-20 object-contain" />
                        ) : (
                          <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground">{product.model_name}</h3>
                              <p className="text-sm text-muted-foreground">{product.sku_code}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded ${
                                product.is_active
                                  ? 'bg-success/10 text-success'
                                  : 'bg-muted text-muted-foreground'
                              }`}>
                                {product.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <Eye className="w-5 h-5 text-muted-foreground" />
                            </div>
                          </div>
                          {product.live_date && (
                            <div className="text-xs text-muted-foreground mb-2">
                              Live Date: {new Date(product.live_date).toLocaleDateString()}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1">
                            {(product.selling_price || product.sale_price) && (
                              <div>
                                <span className="text-muted-foreground">Rate: </span>
                                <span className="font-bold text-emerald-700">₹{(product.selling_price || product.sale_price).toLocaleString()}</span>
                              </div>
                            )}
                            {product.mrp && (
                              <div>
                                <span className="text-muted-foreground">MRP: </span>
                                <span className="font-semibold text-foreground">₹{product.mrp.toLocaleString()}</span>
                              </div>
                            )}
                            {product.flipkart_price && (
                              <div>
                                <span className="text-muted-foreground">FSP: </span>
                                <span className="font-semibold text-success">₹{product.flipkart_price.toLocaleString()}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-muted-foreground">Stock: </span>
                              <span className={`font-bold ${(product.stock_quantity || product.stock || 0) > 0 ? 'text-indigo-900' : 'text-red-500'}`}>
                                {(product.stock_quantity ?? product.stock ?? 0)} units
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Actions: Single Live Toggle & Quick Edit */}
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleProductStatus(product.id, product.is_active);
                        }}
                        disabled={togglingProductId === product.id}
                        className={`text-xs px-2.5 py-1.5 rounded font-medium flex items-center gap-1.5 transition-colors ${
                          product.is_active
                            ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200'
                        }`}
                        title={product.is_active ? 'Click to take offline' : 'Click to make live on website'}
                      >
                        <span className={`w-2 h-2 rounded-full ${product.is_active ? 'bg-emerald-600 animate-pulse' : 'bg-gray-400'}`}></span>
                        <span>{togglingProductId === product.id ? 'Updating...' : (product.is_active ? 'Live on Store' : 'Go Live')}</span>
                      </button>

                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProduct(product);
                          }}
                          className="text-xs px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded border border-indigo-200 flex items-center gap-1"
                          title="Edit Rate, Stock, MRP & Name"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          <span>Edit Rate & Stock</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Variants Tab - Hierarchical Inventory View */}
        {activeTab === 'variants' && (
          <div>
            {variantError && <div role="alert" className="mb-4 p-3 bg-red-50 text-red-800 rounded">{variantError} <button onClick={fetchVariants} className="underline">Retry</button></div>}
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Inventory - SKU → ZUPC → Grade Breakdown</h2>
              <p className="text-sm text-gray-600 mt-1">Available stock by SKU, variant and grade. Use Edit stock & grade to make changes.</p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380] mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading inventory...</p>
              </div>
            ) : filteredVariants.length === 0 ? (
              <div className="bg-muted rounded-lg p-12 text-center">
                <Grid3x3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {searchQuery ? 'No variants match your search' : 'No inventory found'}
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Try different search terms' : 'Inventory is created when devices are purchased'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(getInventoryHierarchy(filteredVariants)).map(([skuId, skuData]) => (
                  <div key={skuId} className="bg-white border rounded-lg overflow-hidden">
                    {/* SKU Level */}
                    <div className="bg-[#2b338015] border-b px-6 py-3 flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">{skuData.name}</h3>
                        <p className="text-sm text-gray-600">SKU ID: {skuId.substring(0, 8)}...</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#2b3380]">{skuData.totalQty}</div>
                        <div className="text-xs text-gray-500">Available Units</div>
                      </div>
                    </div>

                    {/* ZUPC & Grade Level */}
                    <div className="divide-y divide-gray-200">
                      {Object.values(skuData.zupcs).map((zupcData) => (
                        <div key={zupcData.zupc} className="p-4">
                          {/* ZUPC Header */}
                          <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
                            <div>
                              <div className="font-medium text-gray-900">{zupcData.zupc}</div>
                              <div className="text-sm text-gray-500">{zupcData.memory} / {zupcData.color}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-gray-700">{zupcData.totalQty}</div>
                              <div className="text-xs text-gray-500">ZUPC Available</div>
                            </div>
                          </div>

                          {/* Grade Breakdown */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                            {zupcData.grades.map((variant) => (
                              <div 
                                key={variant.id}
                                className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                    variant.sales_grade === 'EXCELLENT' ? 'bg-green-100 text-green-800' :
                                    variant.sales_grade === 'SUPERB' ? 'bg-[#2b338030] text-[#2b3380]' :
                                    variant.sales_grade === 'GOOD' ? 'bg-cyan-100 text-cyan-800' :
                                    variant.sales_grade === 'FAIR' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {variant.sales_grade}
                                  </span>
                                  <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                    variant.sales_category === 'ZOPRO'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-[#2b338030] text-[#2b3380]'
                                  }`}>
                                    {variant.sales_category}
                                  </span>
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-gray-900">{variant.stock_quantity || 0}</div>
                                  <div className="text-xs text-gray-500">available units · {variant.reserved_quantity || 0} reserved</div>
                                </div>
                                <div className="mt-2 text-center">
                                  {variant.is_sold ? (
                                    <span className="text-xs text-gray-600">Sold</span>
                                  ) : variant.sellable_quantity > 0 ? (
                                    <span className="text-xs text-green-600 font-medium">In Stock</span>
                                  ) : (
                                    <span className="text-xs text-red-600">{variant.stock_quantity > 0 ? 'Not available for sale' : 'Out of Stock'}</span>
                                  )}
                                </div>
                                <button type="button" onClick={() => setEditingVariantId(variant.id)} className="mt-3 w-full rounded border border-[#2b3380] px-2 py-2 text-xs font-medium text-[#2b3380] hover:bg-indigo-50">Edit stock &amp; grade</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {editingVariantId && <EditVariantModal variantId={editingVariantId} onClose={() => setEditingVariantId(null)} onSaved={onVariantSaved} />}

      {/* Add Product Modal */}
      <ProductScrapeModal
        isOpen={showScrapeModal}
        onClose={() => setShowScrapeModal(false)}
        category={selectedCategory}
        onSuccess={fetchProducts}
      />

      {/* Bulk Product Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#2b3380]" />
                <h3 className="text-lg font-bold text-gray-900">Bulk Product Upload</h3>
              </div>
              <button
                onClick={() => setShowBulkModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              {/* Template Download Prompt */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-700 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 text-sm">Need the sample CSV format?</h4>
                    <p className="text-xs text-blue-700">Download the template with pre-filled headers and sample device rows.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download Sample CSV
                </button>
              </div>

              {/* Upload Form */}
              <form onSubmit={handleUploadCSV} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select CSV File to Upload
                  </label>
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => {
                      setBulkFile(e.target.files?.[0] || null);
                      setBulkError('');
                      setBulkResult(null);
                    }}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2.5"
                  />
                  {bulkFile && (
                    <p className="text-xs text-green-700 font-medium mt-1">
                      Selected: {bulkFile.name} ({(bulkFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                  <p className="font-semibold text-gray-700">Expected CSV Columns:</p>
                  <p>• <strong>Required:</strong> <code className="text-blue-900">model_name</code>, <code className="text-blue-900">selling_price</code>, <code className="text-blue-900">stock_quantity</code></p>
                  <p>• <strong>Optional:</strong> <code className="text-gray-700">sku_code, brand, category, mrp, sales_grade, memory, color, warranty_days, is_deal_of_the_day, images, description</code></p>
                  <p>• <strong>Note:</strong> Matching SKU or Brand+Model updates existing products; new entries are created immediately.</p>
                </div>

                {bulkUploading && <div role="status">
                  <progress aria-label="CSV upload progress" max="100" value={uploadProgress} style={{ width: '100%' }} />
                  <p>{uploadProgress < 100 ? `Uploading: ${uploadProgress}%` : 'Upload complete. Processing rows…'}</p>
                </div>}
                {bulkError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>{bulkError}</div>
                  </div>
                )}

                {bulkResult && (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                      <div className="flex items-center gap-2 font-bold mb-1">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span>Upload Completed Successfully!</span>
                      </div>
                      <div className="flex gap-4 text-xs font-semibold mt-2">
                        <span className="bg-green-100 px-2 py-1 rounded">Created: {bulkResult.imported_count}</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Updated: {bulkResult.updated_count}</span>
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">Processed: {bulkResult.total_processed}</span>
                      </div>
                    </div>

                    {bulkResult.errors && bulkResult.errors.length > 0 && (
                      <div className="border border-red-200 rounded-lg overflow-hidden">
                        <div className="bg-red-50 px-3 py-2 text-xs font-bold text-red-800">
                          {bulkResult.errors.length} Row(s) Had Issues:
                        </div>
                        <div className="max-h-40 overflow-y-auto text-xs divide-y divide-gray-100">
                          {bulkResult.errors.map((err, idx) => (
                            <div key={idx} className="p-2 flex gap-3 text-red-700">
                              <span className="font-bold">Row {err.row}:</span>
                              <span>[{err.field}] {err.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-3 border-t">
                  <button
                    type="button"
                    onClick={() => setShowBulkModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    {bulkResult ? "Close" : "Cancel"}
                  </button>
                  <button
                    type="submit"
                    disabled={bulkUploading || !bulkFile}
                    className="px-5 py-2 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                  >
                    {bulkUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing CSV...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span>Upload & Import</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {editingProduct && (
        <EditProductModal
          isOpen={Boolean(editingProduct)}
          onClose={() => setEditingProduct(null)}
          product={editingProduct}
          onSuccess={() => {
            fetchProducts();
            setEditingProduct(null);
          }}
        />
      )}
    </Layout>
  );
};

export default ProductManagement;
